import React, { ReactNode, useEffect, useRef, useState } from "react";
import { Table, Checkbox, Select, Tooltip, Button } from "antd";
import {
  CloseCircleFilled,
  CheckCircleFilled,
  CheckCircleOutlined,
  CloudFilled,
  DollarCircleFilled,
  LockFilled,
  CloseCircleOutlined,
  DownOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import "antd/dist/reset.css";
import { ColumnsType } from "antd/es/table";
import { getSubscriptionPlanList, checkoutSubscription, updateSubscriptionPlan } from "api/user.api";
import { notificationController } from "components/Common/Notification/Notification";
import type { CollapseProps } from "antd";
import { Collapse } from "antd";
import SubscriptionAccordion from "./SubscriptionAccordion";
import helpicon from "../../images/helpicon.svg";
import checkCircleBrokenIcon from "../../images/check-circle-broken.svg";
import xCloseIcon from "../../images/x-close.svg";
import checkIcon from "../../images/checkIcon.svg";
import { useSelector } from "react-redux";
import { CheckMarkIcon } from "icons/Icons";
import { BaseOptionType } from "antd/es/select";
import { DefaultOptionType } from "antd/es/cascader";
import { set } from "lodash";

const text = `
  A dog is a type of domesticated animal.
  Known for its loyalty and faithfulness,
  it can be found as a welcome guest in many households across the world.
`;

const items: CollapseProps["items"] = [
  {
    key: "1",
    label: "",
    children: <p>{text}</p>,
  },
  {
    key: "2",
    label: "This is panel header 2",
    children: <p>{text}</p>,
  },
  {
    key: "3",
    label: "This is panel header 3",
    children: <p>{text}</p>,
  },
];

// Correct the type definition to allow JSX.Element or string
interface SubscriptionData {
  key: number;
  feature: string | null;
  [planName: string]: ReactNode | number | string | null; // Dynamic keys for plan names
  className: string;
}

const SubscriptionPlans = () => {
  const selectRef = useRef<any>(null);
  const [customFeatureCounter, setCustomFeatureCounter] = useState<any>([]);
  const [customPriceTotal, setCustomPriceTotal] = useState(0);
  const [basePrice, setBasePrice] = useState(0);
  const [checkedFeatures, setCheckedFeatures] = useState<Record<string, string[]>>({});
  const [subscriptionPlan, setSubscriptionPlan] = useState<any>([]);
  const [subscriptionPlanList, setSubscriptionPlanList] = useState<any>([]);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, Record<string, number>>>(
    {}
  );

  const [selectedPlanIndex, setSelectedPlanIndex] = useState<number | null>(null);

  const userData = useSelector((state: any) => state.user.user);
  const userInfo = useSelector((state: any) => state.user.userInformation);

  const [allPlan, setAllPlan] = useState<any>([]);
  const [planActive, setPlanActive] = useState(false);

  const allPlans = [
    {
      plan_name: "feature",
      price: 0,
      features: [],
    },
    {
      plan_name: "Starter",
      price: 0,
      features: subscriptionPlanList,
    },
    {
      plan_name: "Customize your Subscription",
      price: 0,
      features: subscriptionPlanList,
    },
    {
      plan_name: "Enterprise",
      price: 0,
      features: subscriptionPlanList,
    },
  ];

  const handleSubscription = async () => {
    if (userInfo.products.product_id === "sinecure_plus") {
      if (customFeatureCounter.length > 0) {
        const planIDs = customFeatureCounter.map((item: any) => (item.plan_id));
        const payload = {
          plans: planIDs
        };
        try {
          const response = await updateSubscriptionPlan(payload);
          notificationController.success({
            message: "Your subscription plan updated successfully."
          });
        } catch (error) {
          console.log("Error while updating: ", error);
          notificationController.error({
            message: `${error}`
          });
        }
        
      }
    } else {
      handleCheckoutSubscription();
    }
  };

  const handleCheckoutSubscription = async () => {
    if (customFeatureCounter.length > 0) {
      const formData = customFeatureCounter.map((item: any) => ({
        price: item.plan_id,
        quantity: item.quantity,
      }));
      try {
        const checkout = await checkoutSubscription(formData);
        console.log("Checkout: ", checkout);
        if (checkout.session_url) {
          window.open(checkout.session_url, "_blank");
        }
        setCustomPriceTotal(
          basePrice +
          customFeatureCounter.reduce(
            (total: number, item: any) => total + item.quantity * item.price,
            0
          )
        );
      } catch (error) {
        notificationController.error({ message: `Error occured while checkout: ${error}` });
      }
    } else {
      notificationController.error({
        message: "Please select any plan of your choice to continue.",
      });
    }
  };

  useEffect(() => {
    // if (userData.product.product_id === "sinecure_enterprise") {
    //   setSelectedPlanIndex(3);
    // } else if (userData.product.product_id === "sinecure_plus") {
    //   setSelectedPlanIndex(2);
    // }
    if (userInfo.products.product_id === "sinecure_starter") {
      setPlanActive(true);
    } else {
      setPlanActive(false);
    }
    if (userInfo.products.product_id === "sinecure_enterprise") {
      setSelectedPlanIndex(3);
    } else {
      setSelectedPlanIndex(1);
    }
  }, [userInfo]);

  const handleCustomFeatureCounter = (
    feature_name: string,
    count: number,
    price: number,
    planName: string,
    plan_id: string
  ) => {
    setCustomFeatureCounter((prevState: any) => {
      const existingFeatureIndex = prevState.findIndex(
        (item: any) => item.feature === feature_name
      );

      if (existingFeatureIndex !== -1) {
        const updatedState = [...prevState];
        updatedState[existingFeatureIndex].quantity = count;
        updatedState[existingFeatureIndex].price = price;
        updatedState[existingFeatureIndex].plan_id = plan_id;
        return updatedState;
      } else {
        return [
          ...prevState,
          {
            feature: feature_name,
            quantity: count,
            price: price,
            plan: planName,
            plan_id: plan_id,
          },
        ];
      }
    });
  };

  const handleCustomFeatureCheckBox = (
    feature_name: string,
    planName: string,
    price: number,
    plan_id: string
  ) => {
    setCheckedFeatures((prev) => {
      const featuresForPlan = prev[planName] || [];
      const updatedFeatures = featuresForPlan.includes(feature_name)
        ? featuresForPlan.filter((f) => f !== feature_name)
        : [...featuresForPlan, feature_name];

      return { ...prev, [planName]: updatedFeatures };
    });

    setCustomFeatureCounter((prevState: any) => {
      const exists = prevState.find(
        (item: any) => item.feature === feature_name && item.plan === planName
      );
      if (exists) {
        return prevState.filter((item: any) => item.feature !== feature_name);
      }
      return [
        ...prevState,
        {
          feature: feature_name,
          quantity: 1,
          price: price,
          plan: planName,
          price_id: price,
          plan_id: plan_id,
        },
      ];
    });
  };

  useEffect(() => {
    setCustomPriceTotal(
      basePrice +
      customFeatureCounter.reduce(
        (total: number, item: any) => total + item.quantity * item.price,
        0
      )
    );
  }, [customFeatureCounter]);

  useEffect(() => {
    setCustomPriceTotal(basePrice);
  }, [basePrice]);

  const getSubscriptionPlans = async () => {
    try {
      const response = await getSubscriptionPlanList();
      setSubscriptionPlan(response);
    } catch (error) {
      notificationController.error({ message: `Error occured while fetching Plans: ${error}` });
    }
  };

  useEffect(() => {
    getSubscriptionPlans();
  }, []);

  useEffect(() => {
    console.log("All Plans : ", allPlans);
  }, [allPlans]);

  // Grouping logic
  const groupedData = (inputArray: any) => {
    const predefinedFeatures = [
      { name: "Profile Views", order: 0 },
      { name: "Candidate Export", order: 1 },
      { name: "Email Integration", order: 2 },
      { name: "AI Candidate Screening", order: 3 },
      { name: "ATS", order: 4 }, // Adding ATS as a predefined feature
      { name: "", order: 5 }
    ];

    // Ensure ATS is included even if it's missing from the API
    const featureSet = new Set(inputArray.map((item: any) => item.name));
    predefinedFeatures.forEach((feature) => {
      if (!featureSet.has(feature.name)) {
        inputArray.push({ name: feature.name, price: 0, credits: 0, order: feature.order });
      }
    });

    const result = inputArray.reduce((acc: any, curr: any) => {
      const { name, plan_id, price, credits, order } = curr;

      let group = acc.find((item: any) => item.name === name);
      if (!group) {
        group = { name, pricing: [], price, order };
        acc.push(group);
      }
      group.pricing.push({ plan_id, price, credits, order });
      return acc;
    }, []);

    // Sort according to predefined order
    result.sort((a: any, b: any) => a.order - b.order);
    console.log("sorted groups", result);
    return result;
  };

  // Grouping logic
  const allPlansGroupedData = (inputArray: any) => {
    const preObject = {
      plan_name: "feature",
      price: 0,
      credits: 0,
      features: [],
    };
    const postObject = {
      plan_name: "Need Custom Plans?",
      price: 1000,
      features: subscriptionPlanList,
    };

    const result = inputArray.reduce((acc: any, curr: any) => {
      const { name, price, credits, order } = curr;

      let group = acc.find((item: any) => item.plan_name === `$${price}`);

      if (!group) {
        group = { plan_name: `$${price}`, features: [], pricing: price };
        acc.push(group);
      }

      group.features.push({ name, price, credits, order });
      return acc;
    }, []);

    // Force sorting by predefined order (Profile Views -> Export -> Email -> Screening -> ATS)
    result.forEach((group: any) => {
      group.features.sort((a: any, b: any) => a.order - b.order);
    });

    return [preObject, ...result.filter((group: any) => group.pricing > 100), postObject];
  };

  useEffect(() => {
    setSubscriptionPlanList(groupedData(subscriptionPlan));
    setAllPlan(allPlansGroupedData(subscriptionPlan));
  }, [subscriptionPlan]);

  useEffect(() => {
    console.log("setSubscriptionPlanList: ", subscriptionPlanList);
  }, [subscriptionPlanList]);

  useEffect(() => {
    console.log("setAllPlanList: ", allPlan);
  }, [allPlan]);

  const getPlanIcon = (planName: any) => {
    switch (planName) {
      case "$125":
        return <CheckCircleFilled style={{ color: "#fff", fontSize: "28px" }} />;
      case "$250":
        return <CloudFilled style={{ color: "#fff", fontSize: "28px" }} />;
      case "$500":
        return <DollarCircleFilled style={{ color: "#fff", fontSize: "28px" }} />;
      case "$1000":
        return <LockFilled style={{ color: "#fff", fontSize: "28px" }} />;
      default:
        return null;
    }
  };

  const columns: ColumnsType<SubscriptionData> = allPlans.map((plan: any, index: number) => ({
    // title: plan.plan_name === "feature" ? "" : plan.plan_name,
    title:
      plan.plan_name === "feature" ? (
        ""
      ) : (
        <>
          <div>{plan.plan_name}</div>
        </>
      ),
    dataIndex: plan.plan_name,
    key: index,
    align: plan.plan_name === "feature" ? "left" : "center",
    onHeaderCell: () => ({
      style: {
        color: "#181D27",
        fontSize: 16,
        fontFamily: "Inter",
        fontWeight: "600",
        backgroundColor: "#FFF",
        // ...(index === selectedPlanIndex && { borderLeft: "2px solid #c4f5d1", borderRight: "2px solid #c4f5d1", borderTop: "2px solid #c4f5d1" }), // Outer border for second column header
        ...((index === 1 && planActive) && { borderLeft: "2px solid #c4f5d1", borderRight: "2px solid #c4f5d1", borderTop: "2px solid #c4f5d1" }), // Outer border for second column header
      },
    }),
    onCell: (_, rowIndex) => ({
      style: {
        // ...(index === selectedPlanIndex && { borderLeft: "2px solid #c4f5d1", borderRight: "2px solid #c4f5d1", }), // Outer border for second column cells
        ...((index === 1 && planActive) && { borderLeft: "2px solid #c4f5d1", borderRight: "2px solid #c4f5d1", }), // Outer border for second column cells
        ...(rowIndex === (data.length - 1) && { backgroundColor: "#fcf2fc" }),
        // ...((index === selectedPlanIndex && rowIndex === (data.length - 1)) && { borderBottom: "2px solid #c4f5d1", backgroundColor: "#eafaea" }),
        ...(((index === 1 && planActive) && rowIndex === (data.length - 1)) && { borderBottom: "2px solid #c4f5d1", backgroundColor: "#eafaea" }),
      },
    }),
  }));

  const predefinedOrder: Record<string, number> = {
    "Profile Views": 0,
    "Candidate Export": 1,
    "Email Integration": 2,
    "AI Candidate Screening": 3,
    ATS: 4,
    "": 5
  };

  // Sort features explicitly before mapping
  const sortedFeatures = [...allPlans[allPlans.length - 1].features].sort(
    (a: any, b: any) => (predefinedOrder[a.name] ?? 999) - (predefinedOrder[b.name] ?? 999)
  );

  const data = sortedFeatures.map((feature: any, featureIndex: number) => ({
    key: featureIndex,
    feature: (
      <>
        <span
          style={{
            color: "#181D27",
            fontSize: 14,
            fontFamily: "Inter",
            fontWeight: "500",
            wordWrap: "break-word",
          }}
          className="flex"
        >
          {feature.name}
          {feature.name && (
            <Tooltip title={`Select ${feature.name}`}>
              <img src={helpicon} alt="Help Icon" className="ps-1 cursor-pointer" />
            </Tooltip>
          )}
        </span>
      </>
    ),
    ...Object.fromEntries(
      allPlans.map((plan: any, planIndex: number) => {
        if (plan.plan_name === "feature") return []; // Skip 'feature' and 'Contact and Sales' plan names
        console.log("Features NAme: ", feature.name, planIndex);
        if (feature.name === "" && selectedPlanIndex === planIndex && planActive) {
          return [
            plan.plan_name,
            <div key={planIndex} style={{ display: "flex", justifyContent: "center", gap: "12px" }}>
              <Button
                type="text"
                style={{
                  fontWeight: "bold",
                  borderRadius: "4px",
                  padding: "4px 10px",
                  fontFamily: "Inter",
                  background: "transparent",
                  boxShadow: "none",
                  cursor: "default" // Prevent hover cursor change
                }}
              >
                <img src={checkIcon} /> <span style={{ color: "#147C4B" }}>Your Current Plan</span>
              </Button>
            </div>,
          ];
        }

        if (plan.plan_name === "Customize your Subscription") {
          if (feature.name === "") {
            return [
              plan.plan_name,
              <>
                <div
                  className="flex gap-2"
                  key={planIndex}
                  style={{
                    color: "#344054",
                    fontSize: 16,
                    fontFamily: "Inter",
                    wordWrap: "break-word",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <div
                    className="flex flex-col items-center"
                  >
                    <div>Total Price: <span style={{color: "#344054", fontWeight: "600"}}>${customPriceTotal}</span></div>
                    <div className="text-sm text-gray-500"><span style={{ fontWeight: "normal" }}>*</span>Billed Monthly</div>
                  </div>
                  {/* <div>Total Price: ${customPriceTotal}</div>
              <div className="text-sm text-gray-500"><span style={{ fontWeight: "normal" }}>*</span>Billed Monthly</div> */}
                  <Button
                    className="relative font-semibold text-sm text-white mx-3 py-3 px-4 bg-[#7F56D9] border border-[#7F56D9] flex gap-1 rounded-lg group"
                    onClick={handleSubscription}
                    style={{ fontFamily: "Inter" }}
                  >
                    Upgrade Now
                  </Button>
                </div>
                {/* <div className="text-sm text-gray-500 mt-2">
              <span style={{ fontWeight: "normal", fontSize: 14 }}>*</span>Billed Monthly
            </div> */}
              </>
            ];
          }
        }

        if (plan.plan_name === "Enterprise") {
          if (feature.name === "") {
            return [
              plan.plan_name,
              <>
                <div
                  className="flex gap-2"
                  key={planIndex}
                  style={{
                    color: "#181D27",
                    fontSize: 16,
                    fontFamily: "Inter",
                    fontWeight: "500",
                    wordWrap: "break-word",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >

                  <div
                    style={{ alignItems: "center" }}
                    className="flex justify-end align-items-center"
                  >
                    <Button
                      className="relative font-semibold text-sm text-[#6941C6] py-3 px-4 bg-[#FFF] border border-[#D6BBFB] flex gap-1 rounded-lg group"
                      onClick={() =>
                        window.open("https://share.hsforms.com/15KJkV8HVTjKQHuTWWTnLsgr5699", "_blank")
                      }
                      style={{ fontFamily: "Inter" }}
                    >
                      Contact Sales
                    </Button>
                  </div>


                </div>
                {/* <div className="text-sm text-gray-500 mt-2">
            <span style={{ fontWeight: "normal", fontSize: 14 }}>*</span>Billed Monthly
          </div> */}
              </>
            ];
          }
        }

        if (plan.plan_name === "Starter") {
          if (feature.name === "Profile Views") {
            return [
              plan.plan_name,
              <div
                key={planIndex}
                style={{
                  color: "#181D27",
                  fontSize: 12,
                  fontFamily: "Inter",
                  fontWeight: "600",
                  wordWrap: "break-word",
                }}
              >
                50 Credits
              </div>,
            ];
          } else if (feature.name === "") {
            return [
              plan.plan_name,
              <div
                key={planIndex}
                style={{
                  color: "#181D27",
                  fontSize: 12,
                  fontFamily: "Inter",
                  fontWeight: "500",
                  wordWrap: "break-word",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
              </div>,
            ];
          } else {
            return [
              plan.plan_name,
              <div
                key={planIndex}
                style={{
                  color: "#181D27",
                  fontSize: 12,
                  fontFamily: "Inter",
                  fontWeight: "500",
                  wordWrap: "break-word",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <img src={xCloseIcon} />
              </div>,
            ];
          }
        }
        // Show content only for selected header
        const content =
          feature.pricing[0].credits === null ? (
            <div
              style={{
                color: "#181D27",
                fontSize: 15,
                fontFamily: "Inter",
                fontWeight: "500",
                wordWrap: "break-word",
              }}
            >
              {plan.plan_name === "Enterprise" ? (
                <div
                  style={{
                    color: "#181D27",
                    fontSize: 15,
                    fontFamily: "Inter",
                    fontWeight: "500",
                    wordWrap: "break-word",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <img src={checkCircleBrokenIcon} />
                </div>
              ) : (
                <Checkbox
                  onChange={(e) =>
                    handleCustomFeatureCheckBox(
                      feature.name,
                      plan.plan_name,
                      feature.pricing[0].price,
                      feature.pricing[0].plan_id
                    )
                  }
                  className="feature-checkbox"
                  // disabled={selectedHeader !== plan.plan_name}
                  checked={checkedFeatures[plan.plan_name]?.includes(feature.name)}
                >
                  ${feature.pricing[0].price}
                </Checkbox>
              )}
            </div>
          ) : plan.plan_name === "Enterprise" ? (
            <div
              style={{
                color: "#181D27",
                fontSize: 15,
                fontFamily: "Inter",
                fontWeight: "500",
                wordWrap: "break-word",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img src={checkCircleBrokenIcon} />
            </div>
          ) : (
            <div
              style={{
                color: "#181D27",
                fontSize: 12,
                fontFamily: "Inter",
                fontWeight: "600",
                wordWrap: "break-word",
              }}
            >
              {feature.name !== "ATS" ? (
                <Select
                  ref={selectRef}
                  style={{ width: "60%" }}
                  value={
                    selectedOptions[plan.plan_name]?.[feature.name]
                      ? JSON.stringify(selectedOptions[plan.plan_name][feature.name]) // Ensure value is a string
                      : undefined
                  }
                  onChange={(value: string) => {
                    console.log("Onchange Value : ", value);
                    if (value !== undefined) {
                      const parsedValue = JSON.parse(value); // Convert string back to object

                      setSelectedOptions((prev) => ({
                        ...prev,
                        [plan.plan_name]: {
                          ...(prev[plan.plan_name] || {}),
                          [feature.name]: parsedValue,
                        },
                      }));

                      handleCustomFeatureCounter(
                        feature.name,
                        1,
                        parsedValue.price,
                        plan.plan_name,
                        parsedValue.plan_id
                      );
                    } else {
                      setSelectedOptions((prev) => {
                        const updatedOptions = { ...prev };
                        if (updatedOptions[plan.plan_name]) {
                          delete updatedOptions[plan.plan_name][feature.name];

                          // Remove empty object for plan if no features remain
                          if (Object.keys(updatedOptions[plan.plan_name]).length === 0) {
                            delete updatedOptions[plan.plan_name];
                          }
                        }
                        return updatedOptions;
                      });
                      setCustomFeatureCounter((prevState: any) => {
                        const exists = prevState.find(
                          (item: any) => item.feature === feature.name && item.plan === plan.plan_name
                        );
                        if (exists) {
                          return prevState.filter((item: any) => item.feature !== feature.name);
                        }
                      });
                      return;
                    }
                  }}
                  placeholder="Select"
                  // options={feature.pricing
                  //   .sort((a: any, b: any) => a.order - b.order)
                  //   .map((credit: any) => ({
                  //     value: JSON.stringify({ price: credit.price, plan_id: credit.plan_id }), // Store as string
                  //     label: `${credit.credits} Credits for $${credit.price}`,
                  //   }))}
                  options={Array.from(
                    new Map<string, BaseOptionType | DefaultOptionType>(
                      feature.pricing
                        .sort((a: any, b: any) => a.order - b.order)
                        .map((credit: any) => [
                          JSON.stringify({ price: credit.price, plan_id: credit.plan_id }), // Unique key
                          {
                            value: JSON.stringify({ price: credit.price, plan_id: credit.plan_id }),
                            label: `${credit.credits} Credits for $${credit.price}`,
                          } as BaseOptionType | DefaultOptionType, // Explicit type
                        ])
                    ).values()
                  )}
                  allowClear={feature.name !== 'Profile Views' ? true : false}
                  suffixIcon={
                    feature.name !== 'Profile Views' &&
                      selectedOptions[plan.plan_name]?.[feature.name] ? (
                      <CloseCircleOutlined />
                    ) : undefined
                  }
                // disabled={selectedHeader !== plan.plan_name}
                />
              ) : (
                <div
                  key={planIndex}
                  style={{
                    color: "#181D27",
                    fontSize: 15,
                    fontFamily: "Inter",
                    fontWeight: "500",
                    wordWrap: "break-word",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <img src={xCloseIcon} />
                </div>
              )}
            </div>
          );
        return [plan.plan_name, content];
      })
    ),
  }));

  return (
    <div className="p-5">
      <div className="px-6 pt-4 pb-12 bg-white">
        <Table
          showHeader
          columns={columns}
          dataSource={data}
          pagination={false}
          rowClassName={(record, index) => {
            if (index === data.length - 1) return "last-row"; // Apply 'custom-row' class to the last row
            return index % 2 === 0 ? "row-even" : "row-odd"; // Apply alternate classes for other rows
          }}
          style={{ width: "100%" }}
          bordered={false}
        />
      </div>
    </div>
  );
};

export default SubscriptionPlans;
